import { useState, useEffect, useCallback } from 'react'
import { Trash2, RotateCcw, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'

// Simple relative time formatter
function formatDistanceToNow(date: Date | string): string {
  const now = new Date()
  const then = typeof date === 'string' ? new Date(date) : date
  const diffMs = now.getTime() - then.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffSecs < 60) return 'just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`
  if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`
  return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`
}

interface DeletedDocument {
  id: string
  title: string
  file_path: string
  document_type: string
  deleted_at: string
  tags?: string[]
}

interface TrashViewProps {
  onDocumentClick?: (documentId: string) => void
  onRestore?: (documentId: string) => void
  /** Active workspace org IDs — trash is scoped to these only. */
  workspaceIds?: string[]
}

export function TrashView({ onDocumentClick, onRestore, workspaceIds }: TrashViewProps) {
  const [documents, setDocuments] = useState<DeletedDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  const loadDeletedDocuments = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      const ids = (workspaceIds || []).filter(Boolean)
      if (ids.length > 0) {
        params.set('workspaceIds', ids.join(','))
      }
      const qs = params.toString()
      const response = await fetchWithAuth(
        `/api/documents/deleted${qs ? `?${qs}` : ''}`,
      )
      if (response.ok) {
        const data = await response.json()
        setDocuments(data.documents || [])
      }
    } catch (error) {
      console.error('Failed to load deleted documents:', error)
    } finally {
      setLoading(false)
    }
  }, [workspaceIds])

  useEffect(() => {
    void loadDeletedDocuments()
  }, [loadDeletedDocuments])

  const handleRestore = async (documentId: string) => {
    try {
      const response = await fetchWithAuth(`/api/documents/${documentId}/restore`, {
        method: 'POST'
      })
      
      if (response.ok) {
        const { toast } = await import('sonner')
        toast.success('Document restored', {
          description: 'Document has been restored to its original location'
        })
        
        void loadDeletedDocuments()
        
        window.dispatchEvent(new CustomEvent('document-restored', {
          detail: { documentId }
        }))
        
        if (onRestore) {
          onRestore(documentId)
        }
      } else {
        const errorData = await response.json()
        const { toast } = await import('sonner')
        toast.error('Failed to restore', {
          description: errorData.error || 'Unknown error'
        })
      }
    } catch (error) {
      console.error('Failed to restore document:', error)
      const { toast } = await import('sonner')
      toast.error('Failed to restore', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }

  const filteredDocs = documents.filter(doc =>
    searchQuery === '' ||
    doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.file_path.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid={testIds.trashView.container}
    >
      {/* Header */}
      <div className="shrink-0 p-4 border-b border-border">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Trash</h3>
          <Badge variant="secondary" className="ml-auto">
            {documents.length}
          </Badge>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search deleted documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
            data-testid={testIds.trashView.searchInput}
          />
        </div>
      </div>

      {/* Scrollable list — overflow-y-auto (ScrollArea was not constraining height) */}
      <div
        className="min-h-0 flex-1 overflow-y-auto"
        data-testid={testIds.trashView.list}
      >
        {loading ? (
          <div className="p-4 text-sm text-muted-foreground">Loading...</div>
        ) : filteredDocs.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground text-center">
            {searchQuery ? 'No documents match your search' : 'Trash is empty'}
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredDocs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-start gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer group"
                onClick={() => onDocumentClick?.(doc.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {doc.title}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {doc.file_path}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Deleted {formatDistanceToNow(doc.deleted_at)}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleRestore(doc.id)
                  }}
                  title="Restore"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
